import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { TrendingUp, Users, Activity, ArrowRight, Clock, Lock, Flame, Zap, Sparkles } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const Home = () => {
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [activeEventCount, setActiveEventCount] = useState(0);
  const [categories, setCategories] = useState(null);
  const [eventsError, setEventsError] = useState(false);
  const [categoriesError, setCategoriesError] = useState(false);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchFeaturedEvents();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setCategoriesError(false);
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
    } catch (error) {
      setCategoriesError(true);
    }
  };

  const fetchFeaturedEvents = async () => {
    setLoading(true); setEventsError(false);
    try {
      const response = await api.get('/events?status_filter=active');
      setActiveEventCount(response.data.length);
      // Sort by featured first, then by created date
      const sorted = response.data.sort((a, b) => {
        if (a.featured && !b.featured) return -1;
        if (!a.featured && b.featured) return 1;
        return 0;
      });
      setFeaturedEvents(sorted.slice(0, 4));
    } catch (error) {
      setEventsError(true);
    } finally {
      setLoading(false);
    }
  };

  // Category color scheme - distinct colors for each category
  const getCategoryColor = (category) => {
    const colors = {
      'Approvals': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      'Trial Data': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      'Deals': 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      'Earnings': 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      'Commercial': 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
      'Leadership': 'bg-rose-500/20 text-rose-300 border-rose-500/30',
      'Politics': 'bg-red-500/20 text-red-300 border-red-500/30',
      'Culture': 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
    };
    return colors[category] || 'bg-slate-500/20 text-slate-300 border-slate-500/30';
  };

  // Status indicator - subtle glowing dots
  const getStatusDot = (status) => {
    const dots = {
      active: 'bg-emerald-500 shadow-emerald-500/50',
      upcoming: 'bg-amber-500 shadow-amber-500/50',
      closed: 'bg-slate-500 shadow-slate-500/50',
    };
    return dots[status] || dots.closed;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div
        className="relative overflow-hidden"
        style={{
          background: 'linear-gradient(to bottom right, #020617, #0f172a, #1e1b4b)',
        }}
      >
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1635957644792-aba7dcc9f9c4?crop=entropy&cs=srgb&fm=jpg&q=85')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-24 md:py-32">
          <div className="max-w-3xl">
            <h1
              className="text-4xl md:text-6xl font-extrabold tracking-tight leading-none mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="hero-title"
            >
              Track Pharma Industry
              <span className="block mt-2 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-cyan-400">
                Sentiment in Real-Time
              </span>
            </h1>
            <p className="text-lg leading-relaxed text-slate-400 mb-8 max-w-2xl">
              Vote on pharmaceutical events from PDUFA dates to clinical trials. See what the
              community thinks about the industry's biggest moments.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              {isAuthenticated ? (
                <Link
                  to="/vote"
                  className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white px-8 py-4 rounded-full font-semibold transition-all hover:scale-105 shadow-lg shadow-indigo-500/30"
                  data-testid="hero-start-voting-button"
                >
                  <Flame className="w-5 h-5" />
                  Start Voting
                  <Sparkles className="w-4 h-4" />
                </Link>
              ) : (
                <Link
                  to="/try"
                  className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white px-8 py-4 rounded-full font-semibold transition-all hover:scale-105 shadow-lg shadow-indigo-500/30"
                  data-testid="hero-get-started-button"
                >
                  <Zap className="w-5 h-5" />
                  Try a swipe
                </Link>
              )}
              <Link
                to="/events"
                className="inline-flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 px-8 py-4 rounded-full font-medium transition-all"
                data-testid="hero-browse-events-button"
              >
                Browse Events
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            {!isAuthenticated && <p className="mt-3 text-sm text-slate-300">Explore before signing up.</p>}
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-amber-500/10 border-y border-amber-500/20 py-3">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <p className="text-sm text-amber-400 text-center">
            🚨 For entertainment and educational purposes only. No betting or financial rewards.
          </p>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div
            className="bg-slate-900 border border-slate-800 p-6 rounded-lg"
            data-testid="stat-active-events"
          >
            <Activity className="w-8 h-8 text-indigo-500 mb-3" />
            <p className="text-3xl font-bold font-mono text-white mb-1">
              {loading ? '…' : eventsError ? '—' : activeEventCount}
            </p>
            <p className="text-sm text-slate-400">Active Events</p>
          </div>
          <div
            className="bg-slate-900 border border-slate-800 p-6 rounded-lg"
            data-testid="stat-categories"
          >
            <TrendingUp className="w-8 h-8 text-emerald-500 mb-3" />
            <p className="text-3xl font-bold font-mono text-white mb-1">{categoriesError ? '—' : categories === null ? '…' : categories.length}</p>
            <p className="text-sm text-slate-400">Event Categories</p>
            {categoriesError && <button onClick={fetchCategories} className="min-h-11 text-sm text-indigo-300 underline">Retry categories</button>}
          </div>
          <div
            className="bg-slate-900 border border-slate-800 p-6 rounded-lg"
            data-testid="stat-community"
          >
            <Users className="w-8 h-8 text-cyan-500 mb-3" />
            <p className="text-3xl font-bold font-mono text-white mb-1">{loading ? '…' : eventsError ? '—' : activeEventCount > 0 ? 'Live' : 'Between events'}</p>
            <p className="text-sm text-slate-400">Community Voting</p>
          </div>
        </div>
      </div>

      {/* Quick Vote CTA for logged in users */}
      {isAuthenticated && (
        <div className="max-w-7xl mx-auto px-4 md:px-8 pb-8">
          <Link
            to="/vote"
            className="block bg-gradient-to-r from-indigo-600/20 to-purple-600/20 hover:from-indigo-600/30 hover:to-purple-600/30 border border-indigo-500/30 rounded-2xl p-6 transition-all group"
            data-testid="quick-vote-cta"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center justify-center">
                  <Flame className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    Quick Vote Mode
                    <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full border border-amber-500/30">NEW</span>
                  </h3>
                  <p className="text-slate-400 text-sm">Swipe through events Tinder-style. Vote fast, see results instantly!</p>
                </div>
              </div>
              <ArrowRight className="w-6 h-6 text-indigo-400 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        </div>
      )}

      {/* Featured Events */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2
              className="text-3xl md:text-4xl font-bold tracking-tight"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="featured-events-title"
            >
              Featured Events
            </h2>
            <p className="text-base text-slate-400 mt-2">Vote on trending pharmaceutical events</p>
          </div>
          <Link
            to="/events"
            className="text-sm font-medium text-indigo-400 hover:text-indigo-300 transition-colors"
            data-testid="view-all-events-link"
          >
            View All →
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-400">Loading events...</p>
          </div>
        ) : eventsError ? (
          <div role="alert" className="text-center py-12">
            <p className="text-slate-300">Events could not be loaded. Please try again.</p>
            <button onClick={fetchFeaturedEvents} className="min-h-12 mt-4 px-5 rounded-xl bg-indigo-600">Try again</button>
          </div>
        ) : featuredEvents.length === 0 ? (
          <div className="text-center py-12 text-slate-400">No questions are open right now. <Link to="/events" className="min-h-11 inline-flex items-center text-indigo-300 underline">Explore upcoming questions and past outcomes</Link></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {featuredEvents.map((event) => (
              <Link
                key={event.id}
                to={`/events/${event.id}`}
                className="group relative overflow-hidden rounded-xl border border-slate-800 bg-slate-900/50 backdrop-blur-sm hover:border-indigo-500/50 transition-all duration-300 prediction-card-hover"
                data-testid={`event-card-${event.id}`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2">
                      {/* Status Dot */}
                      <span 
                        className={`w-2 h-2 rounded-full shadow-lg ${getStatusDot(event.status)}`} 
                        title={event.status}
                      />
                      {/* Category Badge */}
                      <span
                        className={`text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full border ${
                          getCategoryColor(event.category)
                        }`}
                      >
                        {event.category}
                      </span>
                    </div>
                    <span className="text-xs text-slate-500 font-mono">{event.company_name}</span>
                  </div>

                  <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-indigo-400 transition-colors">
                    {event.title}
                  </h3>

                  <p className="text-sm text-slate-400 mb-4 line-clamp-2">
                    {event.short_description}
                  </p>

                  {event.vote_stats && event.vote_stats.total_votes > 0 && (
                    <div className="space-y-3">
                      {isAuthenticated ? (
                        <>
                          {/* Option B (Negative) LEFT, Option A (Positive) RIGHT */}
                          <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                            <span>
                              {event.option_b_label}:{' '}
                              <span className="font-mono text-rose-400">
                                {event.vote_stats.option_b_percentage.toFixed(1)}%
                              </span>
                            </span>
                            <span>
                              {event.option_a_label}:{' '}
                              <span className="font-mono text-emerald-400">
                                {event.vote_stats.option_a_percentage.toFixed(1)}%
                              </span>
                            </span>
                          </div>
                          <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden flex">
                            <div
                              className="h-full bg-gradient-to-r from-rose-500 to-rose-600"
                              style={{
                                width: `${event.vote_stats.option_b_percentage}%`,
                              }}
                            />
                            <div
                              className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600"
                              style={{
                                width: `${event.vote_stats.option_a_percentage}%`,
                              }}
                            />
                          </div>
                          <p className="text-xs text-slate-500 font-mono">
                            {event.vote_stats.total_votes} votes
                          </p>
                        </>
                      ) : (
                        <div className="text-center py-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                          <Lock className="w-5 h-5 text-slate-500 mx-auto mb-2" />
                          <p className="text-sm text-slate-400 mb-1">
                            {event.vote_stats.total_votes} people voted
                          </p>
                          <button
                            onClick={(e) => { e.preventDefault(); navigate(`/login?next=${encodeURIComponent(`/events/${event.id}`)}`); }}
                            className="text-xs text-indigo-400 hover:text-indigo-300 font-medium"
                          >
                            Sign up to see results →
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* CTA Section - Only show for non-authenticated users */}
      {!isAuthenticated && (
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
          <div
            className="relative overflow-hidden rounded-2xl p-12 text-center"
            style={{
              background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
            }}
          >
            <div className="relative z-10">
              <h2
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Join the Community
              </h2>
              <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
                Create an account to vote on events and track industry sentiment.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-100 text-slate-900 px-8 py-4 rounded-full font-semibold transition-all hover:scale-105"
                data-testid="cta-signup-button"
              >
                Sign Up Now
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
